import _, { groupBy, flatten, get, last, mapValues, keyBy } from "lodash"
/**
 * The default Router simply steps forward through the questions, according to the QuestionGroups
 */

const buildCondition = (variable, value, [operator], result) => {
  if (variable && operator) {
    if (value) {
      switch (typeof result[variable]) {
        case "number":
        case "boolean":
          return `result.${variable} ${operator} ${value}`
        case "string":
        default:
          return `result.${variable} ${operator} "${value}"`
      }
    } else {
      return `typeof result.${variable} ${operator} "undefined"`
    }
  } else {
    return operator
  }
}

// Although result seems to be unused, it isnt. It is being used in the formed condition
function evaluateCondition(result, condition) {
  let evaluationResult = true
  try {
    // eslint-disable-next-line
    evaluationResult = eval(condition)
  } catch (_error) {
    console.error("Error in evaluation:", condition)
    return false
  }
  return evaluationResult
}

export default (questions, routing) => {
  let routingMap = {}

  // Translate Routing Object to expressions
  const logicalJumps = groupBy(routing, route => route.identifier)

  // Order Groups and connect them
  // Array of identifiers per QuestionPane. Each questionPane should route to the next one.
  let identifierOrder = flatten(
    questions.map(questionGroup => {
      if (questionGroup.identifier) {
        return [
          questionGroup.questions.map(({ question }) =>
            get(question, "identifier")
          )[0],
          last(
            questionGroup.questions.map(({ question }) =>
              get(question, "identifier")
            )
          ),
        ]
        // return questionGroup.identifier
      }
      return questionGroup.questions.map(({ question }) =>
        get(question, "identifier")
      )
    })
  )

  //
  routingMap = identifierOrder.reduce(
    (previousMap, currentIdentifier, currentIndex) => {
      if (Array.isArray(currentIdentifier)) {
        return {
          ...previousMap,
          ...mapValues(
            keyBy(currentIdentifier, value => value),
            () => identifierOrder[currentIndex + 1]
          ),
        }
      } else {
        return {
          ...previousMap,
          [currentIdentifier]: identifierOrder[currentIndex + 1],
        }
      }
    },
    {}
  )

  // For testing purposes to have infinite routing

  return (state, { identifier, _value }) => {
    // On first Run set Start question
    if (state.activeIdentifier === null) {
      // Allows to pass the result of the first question
      const existingKey = Object.keys(state.result).filter(key => {
        if (key && state.result[key]) {
          return true
        } else {
          return false
        }
      })

      if (
        existingKey.length === 1 &&
        identifierOrder.indexOf(existingKey[0]) > -1
      ) {
        state.activeIdentifier = existingKey[0]
        state.history = []
        identifier = existingKey[0]
      } else if (existingKey.length > 1) {
        const firstQuestionKey = get(
          questions[0],
          "questions[0].question.identifier",
          null
        )
        if (existingKey.indexOf(firstQuestionKey) > -1) {
          state.activeIdentifier = firstQuestionKey
          state.history = []
          identifier = firstQuestionKey
        } else {
          return {
            ...state,
            activeIdentifier: get(
              questions[0],
              "questions[0].question.identifier",
              null
            ),
          }
        }
      } else {
        return {
          ...state,
          activeIdentifier: get(
            questions[0],
            "questions[0].question.identifier",
            null
          ),
        }
      }
    }
    if (!identifier) return { ...state }

    let nextQuestionIdentifier =
      routingMap[identifier] || state.activeIdentifier || state.result["type"]

    if (logicalJumps[identifier]) {
      // Logical Conditions for the finished Question exists. So check and navigate accordingly.
      const conditions = logicalJumps[identifier].map(({ conditions }) => {
        const conditionTerm = conditions
          .map(({ variable, value, operator }) =>
            buildCondition(variable, value, operator, state.result)
          )
          .join(" ")
        // Execute the passed conditions
        return evaluateCondition(state.result, conditionTerm || "true")
      })

      // The first condition that matches to true is being used and executes to
      const trueConditionIndex = conditions.indexOf(true)
      if (trueConditionIndex > -1) {
        nextQuestionIdentifier =
          logicalJumps[identifier][trueConditionIndex].targetIdentifier
      }
    }

    let percentage =
      ((identifierOrder.indexOf(nextQuestionIdentifier) + 1) /
        identifierOrder.length) *
      100

    return {
      ...state,
      history: [...state.history, state.activeIdentifier],
      activeIdentifier: nextQuestionIdentifier,
      percentage,
    }
  }
}
